import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { useTranslation, useWindowSize } from '@hooks/index';
// @ts-expect-error @types alias collides with node_modules/@types...
import { PanelType } from '@types/graphql-global-types';
import { getSmPanels_OpraQueries_smPanels_Panel } from '@queries/generated/subject-monitoring.generated';

const HEIGHT = 500;
const WIDTH = 800;

export type TimelinePanelReorderFormValues = {
    selected: PanelListItem[];
};

type PanelListItem = {
    key: string;
    active: boolean;
    /** For UI only (list item title) */
    title: string;
};

type Props = {
    // All sm panels GraphQL response data. Will be filtered and mapped to populate both columns.
    smPanelsResponse: (getSmPanels_OpraQueries_smPanels_Panel | null)[];
    // Ids of panels that the selected column should be populated with when the reset button is used
    defaultSelectedPanelIds: string[];
    // Ids of panels that the selected column should be populated with on load
    selectedPanelIds: string[];
    onCancel: () => void;
    onSubmit: (values: TimelinePanelReorderFormValues) => void;
};

const TimelinePanelReorderDialog = ({
    smPanelsResponse,
    defaultSelectedPanelIds,
    selectedPanelIds,
    onCancel,
    onSubmit,
}: Props) => {
    const { t: tCommon } = useTranslation('common');
    const { t: tReorderDialog } = useTranslation('sm', {
        keyPrefix: 'subjectProfile.timeline.reorderDialog',
    });

    const windowSize = useWindowSize();

    // Map filter sm panels response and map to DragDropList item type
    const allItems = useMemo<PanelListItem[]>(() => {
        const activeTimelinePanels =
            smPanelsResponse
                // Defined panels of type TIMELINE (active or not)
                ?.filter((p) => p != null)
                .filter((p) => p.panelType === PanelType.TIMELINE) ?? [];

        return activeTimelinePanels.map((p) => ({
            key: p.id,
            active: p.active ?? false,
            title: `${p.recordId}: ${p.plotTitle}`,
        }));
    }, [smPanelsResponse]);

    // Get items which should be in the selected column on load
    const defaultValues = useMemo<TimelinePanelReorderFormValues>(() => {
        const allPanelsMap = new Map(allItems.map((p) => [p.key, p]));

        const selected = selectedPanelIds
            .map((id) => allPanelsMap.get(id))
            // Remove any which fail to map
            .filter((p) => p != null);

        return { selected };
    }, [allItems, selectedPanelIds]);

    const {
        control,
        handleSubmit,
        formState: { isDirty, isValid, isSubmitting },
        setValue,
        getValues,
    } = useForm<TimelinePanelReorderFormValues>({
        mode: 'all',
        defaultValues,
    });

    // Get panels for available column (active and not currently selected)

    // Working copy of lists for left column of DragDropListCore component
    // Since the DragDropListCore component keeps track of additional properties like `selected` and `filtered`,
    //   which aren't tracked by the `selectedFields` form value or `availableFields` list
    const [selectedItemsForDragDropList, setSelectedItemsForDragDropList] = useState<DragDropListItemProps[]>(() =>
        getValues('selected'),
    );

    const [availableItemsForDragDropList, setAvailableItemsForDragDropList] = useState<DragDropListItemProps[]>(() => {
        const selectedIdsSet = new Set(getValues('selected').map((p) => p.key));

        return allItems.filter((p) => p.active && !selectedIdsSet.has(p.key));
    });

    const reset = () => {
        const allPanelsMap = new Map(allItems.map((p) => [p.key, p]));
        const defaultSelectedIdsSet = new Set(defaultSelectedPanelIds);

        // Get items for selected column (id in default list). Map id list so the order is correct
        const selected = defaultSelectedPanelIds
            .map((id) => allPanelsMap.get(id))
            // Remove any which fail to map
            .filter((x) => x != null);

        // Get items for available column (active and id not in default list)
        const available = allItems.filter((p) => p.active && !defaultSelectedIdsSet.has(p.key));

        // Update "working" lists
        setSelectedItemsForDragDropList(selected);
        setAvailableItemsForDragDropList(available);

        // Update "true" form value
        setValue('selected', selected, { shouldDirty: true });
    };

    const canSubmit = isDirty && isValid && !isSubmitting;

    return (
        <DraggableDialog
            dataTestId="timeline-panel-reorder-dialog"
            height={Math.min(HEIGHT, windowSize.height)}
            width={Math.min(WIDTH, windowSize.width)}
            top={windowSize.height / 2 - HEIGHT / 2}
            left={windowSize.width / 2 - WIDTH / 2}
            draggable={false}
            onClose={onCancel}
            onAdditionalAction={reset}
            additionalBtnLabel={tCommon('reset')}
            primaryBtnDisabled={!canSubmit}
            onSubmit={handleSubmit(onSubmit) as (e: unknown) => unknown}
            primaryBtnLabel={tCommon('apply')}
            title={tReorderDialog('title')}
        >
            <div style={{ width: '100%', height: '100%' }}>
                <Controller
                    control={control}
                    name="selected"
                    render={({ field: { onChange } }) => (
                        <DragDropListsCore
                            list1Header={tReorderDialog('dragDropListTitles.selectedList')}
                            list2Header={tReorderDialog('dragDropListTitles.availableList')}
                            list1Items={selectedItemsForDragDropList}
                            list2Items={availableItemsForDragDropList}
                            isTwoLists
                            idField="key"
                            onChangeItemsLeft={(items) => {
                                // Update working copy, which includes stuff like `selected` and `filtered`
                                setSelectedItemsForDragDropList(items);

                                // Update true form value, which doesn't include stuff like `selected` and `filtered`
                                // Because those properties would interfere with the form `isDirty` detection
                                const mapped = items.map(({ selected, filtered, ...rest }) => rest);
                                onChange(mapped);
                            }}
                            onChangeItemsRight={setAvailableItemsForDragDropList}
                        />
                    )}
                />
            </div>
        </DraggableDialog>
    );
};

export default TimelinePanelReorderDialog;
